import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, Heart, ShoppingBag, User, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../ui/sheet";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { useStore } from "../../lib/store";

export const NAV = [
  { label: "Home", to: "/" },
  { label: "Shop All", to: "/shop" },
  { label: "Fashion", to: "/shop?category=Fashion" },
  { label: "Accessories", to: "/shop?category=Accessories" },
  { label: "Home & Living", to: "/shop?category=Home%20%26%20Living" },
  { label: "Gifts", to: "/shop?category=Gifts" },
  { label: "New Arrivals", to: "/shop?tag=new" },
  { label: "Sale", to: "/shop?tag=sale" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const IconBtn = ({ to, onClick, label, count, children, testId }) => {
  const inner = (
    <>
      {children}
      {count > 0 && (
        <span data-testid={`${testId}-count`} className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-magenta px-1 text-[10px] font-bold text-white">
          {count}
        </span>
      )}
    </>
  );
  const cls = "relative grid h-10 w-10 place-items-center rounded-full text-plum transition-colors hover:bg-plum/5 hover:text-magenta";
  return to ? (
    <Link to={to} aria-label={label} data-testid={testId} className={cls}>{inner}</Link>
  ) : (
    <button onClick={onClick} aria-label={label} data-testid={testId} className={cls}>{inner}</button>
  );
};

const SearchBar = ({ onClose }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    onClose();
  };
  return (
    <form onSubmit={submit} className="mx-auto flex max-w-2xl items-center gap-3 border-b border-plum/20 py-3">
      <Search className="h-5 w-5 text-plum/60" />
      <input
        autoFocus
        data-testid="header-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search blazers, gold hoops, candles..."
        className="flex-1 bg-transparent font-display text-lg italic text-plum placeholder:text-plum/45 focus:outline-none"
      />
      <button type="button" onClick={onClose} aria-label="Close search" data-testid="header-search-close"><X className="h-5 w-5" /></button>
    </form>
  );
};

export const Header = () => {
  const { cartCount, wishlist } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div data-testid="announcement-bar" className="bg-plum py-2.5 text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] sm:tracking-[0.28em] text-cream">
        Free shipping on orders $75+ <span className="mx-2 sm:mx-3 text-gold">|</span>
        <Link to="/shop" className="text-gold-light underline-offset-4 hover:underline" data-testid="announcement-shop-link">Shop now ✦</Link>
      </div>
      <header className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-cream/85 backdrop-blur-xl shadow-[0_10px_40px_-20px_rgba(42,15,46,.35)]" : "bg-cream"}`}>
        <div className="mx-auto grid max-w-7xl grid-cols-[1fr_auto_1fr] items-center px-4 sm:px-8 pt-6 pb-3">
          <div className="flex items-center gap-1">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button aria-label="Open menu" data-testid="header-menu-button" className="grid h-10 w-10 place-items-center rounded-full hover:bg-plum/5 lg:hidden">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] max-w-sm border-none bg-plum text-cream">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="mt-10 flex flex-col gap-1">
                  {NAV.map((n) => (
                    <Link key={n.label} to={n.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-nav-${slug(n.label)}`} className="border-b border-cream/10 py-3.5 font-display text-2xl hover:text-gold transition-colors">
                      {n.label}
                    </Link>
                  ))}
                </div>
                <p className="mt-10 font-script text-4xl text-gold-light">Luxury Looks. Everyday Finds.</p>
              </SheetContent>
            </Sheet>
            <span className="hidden lg:block eyebrow text-[10px] text-plum/60">Style • Living • Everyday Luxe</span>
          </div>
          <Logo />
          <div className="flex items-center justify-end gap-0.5 sm:gap-1">
            <IconBtn label="Search" onClick={() => setSearchOpen((s) => !s)} testId="header-search-button"><Search className="h-[19px] w-[19px]" /></IconBtn>
            <span className="hidden sm:block">
              <IconBtn label="Account" onClick={() => toast("Accounts are coming soon to Luxe Dream Market")} testId="header-account-button"><User className="h-[19px] w-[19px]" /></IconBtn>
            </span>
            <span className="hidden sm:block">
              <IconBtn to="/wishlist" label="Wishlist" count={wishlist.length} testId="header-wishlist-button"><Heart className="h-[19px] w-[19px]" /></IconBtn>
            </span>
            <IconBtn to="/cart" label="Cart" count={cartCount} testId="header-cart-button"><ShoppingBag className="h-[19px] w-[19px]" /></IconBtn>
          </div>
        </div>
        <nav className="hidden lg:block">
          <ul className="mx-auto flex max-w-5xl items-center justify-center gap-9 pb-4 pt-1">
            {NAV.map((n) => (
              <li key={n.label}>
                <NavLink
                  to={n.to}
                  end
                  data-testid={`nav-${slug(n.label)}`}
                  className={`group relative text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors hover:text-magenta ${n.label === "Sale" ? "text-tangerine-deep" : "text-plum"}`}
                >
                  {n.label}
                  <span className="absolute -bottom-1.5 left-0 h-[1.5px] w-full origin-left scale-x-0 bg-magenta transition-transform duration-500 group-hover:scale-x-100" />
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        {searchOpen && (
          <div className="border-t border-plum/10 bg-cream px-4 pb-4">
            <SearchBar onClose={() => setSearchOpen(false)} />
          </div>
        )}
      </header>
    </>
  );
};
